import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Clock, AlertTriangle, X, Mail, Phone, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { API_ENDPOINTS } from '../config/api';
import { useNavbar } from '../context/NavbarContext';
import { getTokenPayload } from '../utils/jwtUtils';

// Support contact details
const SUPPORT_EMAIL = 'support@neurolaw.ai';
const SUPPORT_PHONE = '+91 9001239773';
const SUPPORT_PHONE_DISPLAY = '+91 9001239773';

// Support Modal Component
const SupportModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Upgrade Your Plan</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            To upgrade your plan or learn more about our pricing options, please contact our support team or visit our pricing page.
          </p>

          {/* Contact Info */}
          <div className="space-y-3 bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900">Contact Support</h3>
            
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Mail size={16} className="text-gray-600" />
              </div>
              <span>{SUPPORT_EMAIL}</span>
            </a>

            <a
              href={`tel:${SUPPORT_PHONE}`}
              className="flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Phone size={16} className="text-gray-600" />
              </div>
              <span>{SUPPORT_PHONE_DISPLAY}</span>
            </a>
          </div>

          {/* Pricing Link */}
          <a
            href="/pricing"
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <span>View Pricing Plans</span>
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Footer */}
        <button
          onClick={onClose}
          className="w-full mt-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const TrialBanner = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { showNavbar, sidebarExpanded } = useNavbar();
  const [userDetails, setUserDetails] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Check if user is org member - if so, hide banner entirely
  const isOrgMember = useMemo(() => {
    const payload = getTokenPayload();
    if (!payload) return false;
    
    // Only show banner for individual users and org owners
    // Hide for org members (isOrgUser=true and not org owner)
    return payload.isOrgUser === true && payload.accountType !== 'organization';
  }, []);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get(API_ENDPOINTS.AUTH.GET_USER_DETAILS);
      if (response?.success) {

        setUserDetails(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user details for trial banner:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Calculate countdown
  useEffect(() => {
    if (!userDetails?.expiryTime) return;

    const calculateCountdown = () => {
      const now = new Date().getTime();
      const expiryTime = new Date(userDetails.expiryTime).getTime();
      const difference = expiryTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        setCountdown(null);
        return;
      }

      setIsExpired(false);
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);

    return () => clearInterval(interval);
  }, [userDetails?.expiryTime]);

  // Don't show if not authenticated, loading, or dismissed
  if (!isAuthenticated || loading || isDismissed) return null;

  // Don't show if no user details or no navbar
  if (!userDetails || !showNavbar) return null;

  // Don't show banner for org members - their wallet is managed by the organization
  if (isOrgMember) return null;

  const { credits, expiryTime, subscriptionType } = userDetails;
  const isSubscriptionExpired = !expiryTime || isExpired;
  const isPlan = subscriptionType === 'plan';

  // Don't show banner for plan expiry - only for coupon/free trial
  if (isPlan) return null;

  // Case 1: Expired but still has credits - don't show anything
  if (isSubscriptionExpired && credits > 0) return null;

  // Sidebar width for positioning
  const sidebarWidth = sidebarExpanded ? '240px' : '64px';

  // Case 2: Expired AND no credits - show "subscription ended" message
  if (isSubscriptionExpired && credits === 0) {
    return (
      <>
        <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
        
        {/* Desktop Banner */}
        <div 
          className="fixed top-0 right-0 bg-gray-900 border-b border-gray-700 px-3 py-1.5 z-[999] transition-all duration-300 hidden lg:block"
          style={{ left: sidebarWidth }}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
              <span className="text-xs font-medium text-white">
                {isPlan ? 'Your plan has expired' : 'Your free trial has ended'}
              </span>
              <span className="text-xs text-gray-400 hidden sm:inline">
                — {isPlan ? 'Renew your plan to continue' : 'Subscribe to continue using the platform'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSupportModal(true)}
                className="px-3 py-1 bg-white text-gray-900 text-xs font-medium rounded hover:bg-gray-100 transition-colors"
              >
                Contact Support
              </button>
              <button
                onClick={() => setIsDismissed(true)}
                className="p-0.5 text-gray-400 hover:text-white transition-colors"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Banner - below navbar */}
        <div className="fixed top-16 left-0 right-0 bg-gray-900 border-b border-gray-700 px-3 py-1.5 z-[999] lg:hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
              <span className="text-xs font-medium text-white truncate">
                {isPlan ? 'Plan expired' : 'Free trial ended'}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowSupportModal(true)}
                className="px-2 py-1 bg-white text-gray-900 text-xs font-medium rounded hover:bg-gray-100 transition-colors"
              >
                Contact Support
              </button>
              <button
                onClick={() => setIsDismissed(true)}
                className="p-0.5 text-gray-400 hover:text-white transition-colors"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Case 3: Not expired - show countdown
  if (countdown && !isSubscriptionExpired) {
    const { days, hours, minutes, seconds } = countdown;
    
    // Determine urgency level for styling
    const isUrgent = days === 0 && hours < 24;
    const isCritical = days === 0 && hours < 6;

    // Common countdown badges component
    const CountdownBadges = ({ compact = false }) => (
      <div className="flex items-center gap-1">
        {days > 0 && (
          <span className={`${compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-xs'} rounded font-bold ${
            isCritical 
              ? 'bg-red-900/50 text-red-300' 
              : isUrgent 
                ? 'bg-yellow-900/50 text-yellow-300' 
                : 'bg-gray-800 text-white'
          }`}>
            {days}d
          </span>
        )}
        <span className={`${compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-xs'} rounded font-bold ${
          isCritical 
            ? 'bg-red-900/50 text-red-300' 
            : isUrgent 
              ? 'bg-yellow-900/50 text-yellow-300' 
              : 'bg-gray-800 text-white'
        }`}>
          {String(hours).padStart(2, '0')}h
        </span>
        <span className={`${compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-xs'} rounded font-bold ${
          isCritical 
            ? 'bg-red-900/50 text-red-300' 
            : isUrgent 
              ? 'bg-yellow-900/50 text-yellow-300' 
              : 'bg-gray-800 text-white'
        }`}>
          {String(minutes).padStart(2, '0')}m
        </span>
        <span className={`${compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-xs'} rounded font-bold ${
          isCritical 
            ? 'bg-red-900/50 text-red-300' 
            : isUrgent 
              ? 'bg-yellow-900/50 text-yellow-300' 
              : 'bg-gray-800 text-white'
        }`}>
          {String(seconds).padStart(2, '0')}s
        </span>
      </div>
    );

    return (
      <>
        <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
        
        {/* Desktop Banner */}
        <div 
          className={`fixed top-0 right-0 border-b px-3 py-1.5 z-[999] transition-all duration-300 hidden lg:block ${
            isCritical 
              ? 'bg-gray-900 border-red-600' 
              : isUrgent 
                ? 'bg-gray-900 border-yellow-600' 
                : 'bg-gray-100 border-gray-300'
          }`}
          style={{ left: sidebarWidth }}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Clock size={14} className={`flex-shrink-0 ${
                isCritical 
                  ? 'text-red-400' 
                  : isUrgent 
                    ? 'text-yellow-400' 
                    : 'text-gray-600'
              }`} />
              <span className={`text-xs font-medium ${
                isCritical || isUrgent ? 'text-white' : 'text-gray-700'
              }`}>
                {isPlan ? 'Plan expires in:' : 'Trial ends in:'}
              </span>
              <CountdownBadges />
              
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.href = '/pricing'}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  isCritical 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : isUrgent 
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {isPlan ? 'Renew' : 'Upgrade'}
              </button>
              <button
                onClick={() => setIsDismissed(true)}
                className={`p-0.5 transition-colors ${
                  isCritical || isUrgent 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Banner - below navbar */}
        <div 
          className={`fixed top-16 left-0 right-0 border-b px-2 py-1.5 z-[999] lg:hidden ${
            isCritical 
              ? 'bg-gray-900 border-red-600' 
              : isUrgent 
                ? 'bg-gray-900 border-yellow-600' 
                : 'bg-gray-100 border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Clock size={12} className={`flex-shrink-0 ${
                isCritical 
                  ? 'text-red-400' 
                  : isUrgent 
                    ? 'text-yellow-400' 
                    : 'text-gray-600'
              }`} />
              <CountdownBadges compact />
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => window.location.href = '/pricing'}
                className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                  isCritical 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : isUrgent 
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {isPlan ? 'Renew' : 'Upgrade'}
              </button>
              <button
                onClick={() => setIsDismissed(true)}
                className={`p-0.5 transition-colors ${
                  isCritical || isUrgent 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Dismiss"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default TrialBanner;
